<template>
  <div class="v2026-aifc" v-if="hasFc">

    <div class="v2026-aifc__head">
      <span class="v2026-ai-mask v2026-aifc__head-ico" aria-hidden="true"
            :style="maskStyle(icons.document)"></span>
      <span class="v2026-aifc__label">สกัดข้อมูลการจอง</span>
      <button type="button" class="v2026-aifc__ghost" v-if="hasQueue"
              :disabled="fcLoading" @click="clearAll">ล้างทั้งหมด</button>
    </div>

    <div class="v2026-ai-card v2026-aifc__card" :aria-busy="fcLoading ? 'true' : 'false'">

      <template v-if="hasQueue">
        <div class="v2026-aifc__metarow">
          <span class="v2026-aifc__meta">ข้อความที่ส่งให้ AI · {{ queueCount }} ข้อความ</span>
          <button type="button" class="v2026-aifc__ghost v2026-aifc__ghost--sm" v-if="hasOutcome"
                  :aria-expanded="queueOpen ? 'true' : 'false'"
                  @click="queueOpen = !queueOpen">{{ queueOpen ? 'ย่อ' : 'ขยาย' }}</button>
        </div>

        <ul class="v2026-aifc__chips" v-if="showChips" aria-label="ข้อความที่ส่งให้ AI">
          <li class="v2026-aifc__chip" :class="{ 'is-dimmed': fcLoading }"
              v-for="(message, key) in multiMessages" :key="key">
            <span class="v2026-aifc__chip-text">{{ message }}</span>
            <button type="button" class="v2026-aifc__chip-x"
                    :disabled="fcLoading"
                    :aria-disabled="fcLoading ? 'true' : 'false'"
                    :aria-label="removeLabel(message)"
                    @click="removeOne(key)">
              <!-- Glyph, not a mask icon: icons2026 ships no close.svg. -->
              <span aria-hidden="true">×</span>
            </button>
          </li>
        </ul>

        <button type="button" class="v2026-aifc__primary" :disabled="fcLoading" @click="analyse">
          <span class="v2026-ai-mask v2026-aifc__primary-ico" aria-hidden="true"
                :style="maskStyle(icons.sparkles)"></span>
          <span class="v2026-aifc__primary-text">{{ fcLoading ? 'กำลังวิเคราะห์…' : 'ช่วยฉันวิเคราะห์' }}</span>
        </button>

        <div class="v2026-ai__loading" v-if="fcLoading">
          <span class="v2026-ai__spinner" aria-hidden="true"></span>
          <span>กำลังวิเคราะห์ข้อความ<span class="v2026-ai__dots"><i></i><i></i><i></i></span></span>
        </div>

        <!-- Persists after the toast auto-hides; the button stays enabled so the operator can link
             the account in another tab and retry. -->
        <template v-else-if="fcError === 'denied'">
          <div class="v2026-aifc__error" role="alert">⚠ ลูกค้ายังไม่ได้ลงทะเบียนเป็นสมาชิก</div>
          <div class="v2026-aifc__meta">จึงวิเคราะห์ข้อมูลการจองไม่ได้</div>
        </template>
      </template>

      <!-- The queue emptied, but an outcome may still be on screen below. -->
      <template v-else>
        <div class="v2026-ai__bullet">• ยังไม่มีข้อความในคิว</div>
        <div class="v2026-aifc__meta">กด "ให้ AI สกัดข้อมูลจอง" ใต้ข้อความในแชท เพื่อส่งข้อความเข้ามาที่นี่</div>
      </template>

    </div>

    <template v-if="!fcLoading">

      <div class="v2026-ai-card v2026-aifc__card" v-if="fcError === 'empty'" role="status" aria-live="polite">
        <div class="v2026-aifc__title-line">
          <span class="v2026-ai-mask v2026-aifc__head-ico" aria-hidden="true"
                :style="maskStyle(icons.document)"></span>
          <span>ไม่พบข้อมูลการจองในข้อความนี้</span>
        </div>
        <div class="v2026-aifc__text">ลองเพิ่มข้อความที่มีวันที่ เวลา หรือที่อยู่ แล้ววิเคราะห์อีกครั้ง</div>
        <div class="v2026-aifc__actions">
          <button type="button" class="v2026-aifc__outline" :disabled="!hasQueue" @click="analyse">
            <span class="v2026-ai-mask v2026-aifc__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.retry)"></span>
            <span>ลองใหม่</span>
          </button>
        </div>
      </div>

      <div class="v2026-ai-card v2026-aifc__card" v-else-if="fcError === 'unreadable'" role="status" aria-live="polite">
        <div class="v2026-aifc__title-line">
          <span class="v2026-ai-mask v2026-aifc__head-ico" aria-hidden="true"
                :style="maskStyle(icons.document)"></span>
          <span>AI อ่านข้อมูลจากข้อความนี้ไม่ได้</span>
        </div>
        <div class="v2026-aifc__text">AI รองรับเฉพาะ ข้อมูลการจอง ใบกำกับภาษี และเลขที่การจอง</div>
        <div class="v2026-aifc__meta" v-if="functionName">ประเภทที่ระบบตอบกลับ: {{ functionName }}</div>
        <div class="v2026-aifc__actions">
          <button type="button" class="v2026-aifc__outline" :disabled="!hasQueue" @click="analyse">
            <span class="v2026-ai-mask v2026-aifc__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.retry)"></span>
            <span>ลองใหม่</span>
          </button>
        </div>
      </div>

      <div class="v2026-ai-card v2026-aifc__card" v-else-if="requestFailed" role="status" aria-live="polite">
        <div class="v2026-aifc__error" role="alert">⚠ วิเคราะห์ข้อความไม่สำเร็จ</div>
        <div class="v2026-aifc__meta">ยังไม่ได้วิเคราะห์ข้อความในคิว ลองใหม่อีกครั้ง</div>
        <div class="v2026-aifc__actions">
          <button type="button" class="v2026-aifc__outline" :disabled="!hasQueue" @click="analyse">
            <span class="v2026-ai-mask v2026-aifc__outline-ico" aria-hidden="true"
                  :style="maskStyle(icons.retry)"></span>
            <span>ลองใหม่</span>
          </button>
        </div>
      </div>

      <BookingResultV2026 v-else-if="fcShape === 'booking'" :result="fcResult" />

      <TaxInvoiceResultV2026 v-else-if="fcShape === 'tax'" :result="fcResult" />

      <div class="v2026-ai-card v2026-aifc__card" v-else-if="fcShape === 'order'" role="status" aria-live="polite">
        <div class="v2026-aifc__text">
          ข้อความนี้อ้างถึงรายการจอง <span class="v2026-aifc__strong">#{{ orderCode }}</span>
        </div>
        <a class="v2026-aifc__link" v-if="actionUrl"
           :href="actionUrl" :title="actionUrl" target="_blank" rel="noopener noreferrer">
          <span>เปิดรายละเอียดการจอง</span>
          <span class="v2026-ai-mask v2026-aifc__link-ico" aria-hidden="true"
                :style="maskStyle(icons.externalLink)"></span>
        </a>
      </div>

    </template>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import BookingResultV2026 from '@/components/coversation/BookingResultV2026.vue'
import TaxInvoiceResultV2026 from '@/components/coversation/TaxInvoiceResultV2026.vue'

export default {
  name: 'ai-function-calling-v2026',
  components: {
    BookingResultV2026,
    TaxInvoiceResultV2026
  },
  data () {
    return {
      icons: {
        document: require('@/assets/icons2026/document.svg'),
        sparkles: require('@/assets/icons2026/ai-magic.svg'),
        retry: require('@/assets/icons2026/refresh.svg'),
        externalLink: require('@/assets/icons2026/external-link.svg')
      },
      // Folded once a result exists, so the form stays above the fold.
      queueOpen: false
    }
  },
  computed: {
    ...mapState('aiAssistant', ['multiMessages', 'fcRequest', 'fcLoading', 'fcResult', 'fcError']),
    ...mapState('conversation', ['customer']),
    queueCount () {
      return Array.isArray(this.multiMessages) ? this.multiMessages.length : 0
    },
    hasQueue () {
      return this.queueCount > 0
    },
    hasFc () {
      return !!(this.hasQueue || this.fcRequest || this.fcLoading || this.fcResult || this.fcError)
    },
    // 'denied' is not an outcome: it never replaces the queue view, it annotates it.
    hasOutcome () {
      return !!(this.fcResult || (this.fcError && this.fcError !== 'denied'))
    },
    showChips () {
      return !this.hasOutcome || this.queueOpen
    },
    requestFailed () {
      return this.fcError === 'server' || this.fcError === 'network'
    },
    fcParams () {
      return (this.fcResult && this.fcResult.params) || {}
    },
    functionName () {
      return (this.fcResult && this.fcResult.function_name) || null
    },
    actionUrl () {
      return (this.fcResult && this.fcResult.action_url) || null
    },
    orderCode () {
      return this.fcParams.order_code || null
    },
    fcShape () {
      if (!this.fcResult) return null
      if (this.functionName === 'get_booking_value') {
        return (this.fcResult.service && this.fcResult.params) ? 'booking' : null
      }
      if (this.functionName === 'get_tax_invoice') {
        return Array.isArray(this.fcResult.user_company_addresses) ? 'tax' : null
      }
      if (this.functionName === 'get_order_code') {
        return (this.orderCode || this.actionUrl) ? 'order' : null
      }
      return null
    }
  },
  methods: {
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    removeLabel (message) {
      return `ลบข้อความนี้ออกจากคิว: ${String(message || '').slice(0, 30)}`
    },
    removeOne (key) {
      if (this.fcLoading) return
      this.$store.commit('aiAssistant/REMOVE_ONE_OF_MULTI_MESSAGES', key)
    },
    clearAll () {
      if (this.fcLoading) return
      this.$store.commit('aiAssistant/CLEAR_MULTI_MESSAGES')
      this.$store.commit('aiAssistant/CLEAR_FC')
      // CLEAR_FC nulls fcNotice, so the toast has to be raised after it.
      this.$store.commit('aiAssistant/SET_FC_NOTICE', { kind: 'cleared' })
    },
    analyse () {
      if (this.fcLoading || !this.hasQueue) return
      if (!(this.customer && this.customer.customer)) {
        this.$store.commit('aiAssistant/SET_FC_ERROR', 'denied')
        this.$store.commit('aiAssistant/SET_FC_NOTICE', { kind: 'denied' })
        return
      }
      this.$store.dispatch('aiAssistant/callAiFcExtract', {
        beneat_user_id: this.customer.customer.id,
        // A single space, not a newline — the shape the endpoint expects.
        user_input: this.multiMessages.join(' ')
      })
    }
  }
}
</script>

<style scoped>
.v2026-ai-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}
.v2026-ai-card {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}

.v2026-aifc {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}

.v2026-aifc__head {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-aifc__head-ico {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
}
.v2026-aifc__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}

.v2026-aifc__ghost {
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
}
.v2026-aifc__ghost--sm {
  padding: var(--v2026-sp-1) var(--v2026-sp-2);
  font-size: var(--v2026-fs-meta);
}
.v2026-aifc__ghost:hover {
  background: var(--v2026-surface-hover);
}
.v2026-aifc__ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-aifc__ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

.v2026-aifc__card {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
}

.v2026-aifc__metarow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-2);
}

.v2026-aifc__chips {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
.v2026-aifc__chip {
  display: flex;
  align-items: flex-start;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-2) var(--v2026-sp-2) var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: var(--v2026-r-tab);
  background: var(--v2026-surface);
}
.v2026-aifc__chip:hover {
  border-color: var(--v2026-color-main);
}
.v2026-aifc__chip.is-dimmed {
  opacity: 0.65;
}
.v2026-aifc__chip-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  line-height: 1.5;
  word-break: break-word;
  /* Three lines, then ellipsis: a pasted address must not push the CTA off the fold. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.v2026-aifc__chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}
.v2026-aifc__chip-x:hover {
  color: #B42318;
}
.v2026-aifc__chip-x:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-aifc__chip-x:disabled {
  cursor: default;
}
.v2026-aifc__chip-x {
  color: var(--v2026-text-muted);
  font-size: 18px;
  line-height: 1;
}

.v2026-aifc__primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--v2026-sp-2);
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: linear-gradient(135deg, #2f6bff 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
}
.v2026-aifc__primary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.30);
}
.v2026-aifc__primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.v2026-aifc__primary-ico {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  background-color: #ffffff;
}
.v2026-aifc__primary-text {
  white-space: nowrap;
}

.v2026-aifc__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-2);
}
.v2026-aifc__outline {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
}
.v2026-aifc__outline:hover {
  background: var(--v2026-surface-hover);
}
.v2026-aifc__outline:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-aifc__outline:disabled {
  opacity: 0.6;
  cursor: default;
}
.v2026-aifc__outline-ico {
  width: 14px;
  height: 14px;
  background-color: currentColor;
}

.v2026-aifc__text {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-aifc__strong {
  color: var(--v2026-text);
  font-weight: 600;
}
.v2026-aifc__meta {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-aifc__title-line {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
/* Own red instead of .v2026-ai__error: #f04438 on the sunken surface misses AA for body text. */
.v2026-aifc__error {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: #B42318;
  line-height: 1.5;
  word-break: break-word;
}

.v2026-aifc__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-2);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-link);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-aifc__link:hover {
  text-decoration: underline;
}
.v2026-aifc__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 4px;
}
.v2026-aifc__link-ico {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  background-color: currentColor;
}

/* Loading + bullet recipes shared with the vision block, so both AI results animate identically. */
.v2026-ai__bullet {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-ai__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--v2026-text-muted);
}
.v2026-ai__spinner {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  border: 2px solid rgba(124, 58, 237, 0.25);
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: v2026-aifc-spin 0.7s linear infinite;
}
@keyframes v2026-aifc-spin {
  to { transform: rotate(360deg); }
}
.v2026-ai__dots i {
  display: inline-block;
  width: 3px;
  height: 3px;
  margin-left: 2px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.3;
  animation: v2026-aifc-dot 1.2s infinite;
}
.v2026-ai__dots i:nth-child(2) { animation-delay: 0.2s; }
.v2026-ai__dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes v2026-aifc-dot {
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
}
</style>
